import { Link } from "react-router-dom";
import React, { useState, useRef } from "react";
import { Overlay, Popover } from "react-bootstrap";
import { MoreVertical, Search } from "lucide-react";
import {
  formatCautionFeeStatus,
  getCautionFeeStatusStyle,
} from "../../../helpers/statusStyles";
import "./CautionFeeTable.css";

const CautionFeeActions = ({ id, name }: { id: string; name: string }) => {
  const [open, setOpen] = useState(false);
  const target = useRef<HTMLButtonElement>(null);
  return <>
    <button ref={target} type="button" className="cf-actions-trigger"
      aria-label={`Actions for ${name}`} aria-expanded={open} aria-haspopup="true"
      onClick={() => setOpen(!open)} onKeyDown={event => { if (event.key === "Escape") setOpen(false); }}>
      <MoreVertical size={22} />
    </button>
    <Overlay target={target.current} show={open} placement="bottom-end" rootClose onHide={() => setOpen(false)}>
      <Popover id={`caution-actions-${id}`} className="cf-actions-popover"
        onKeyDown={event => { if (event.key === "Escape") { setOpen(false); target.current?.focus(); } }}>
        <Popover.Body>
          <Link autoFocus to={`/caution-fee/${encodeURIComponent(id)}`} onClick={() => setOpen(false)}>
            <Search size={22} /> View More
          </Link>
        </Popover.Body>
      </Popover>
    </Overlay>
  </>;
};

type BookingType = "instant" | "non-instant";

interface CautionFeeData {
  transactionId: string;
  bookingType: BookingType;
  propertyName: string;
  stayDates: { label: string };
  cautionFee: number;
  displayStatus:
    | "Eligible"
    | "Under review"
    | "Disputed"
    | "Held"
    | "Paid"
    | string;
}

interface PaginationData {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
  from: number;
  to: number;
  has_more_pages: boolean;
}

interface Props {
  showBookingType?: boolean;
  data: CautionFeeData[];
  pagination?: PaginationData;
  onPageChange?: (page: number) => void;
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const style = getCautionFeeStatusStyle(status);
  return (
    <span
      style={{
        ...style,
        display: "inline-block",
        padding: "5px 18px",
        borderRadius: "20px",
        fontSize: "13px",
        fontWeight: 500,
      }}
    >
      {formatCautionFeeStatus(status)}
    </span>
  );
};

const BookingTypeCell: React.FC<{ type: BookingType }> = ({ type }) => {
  const isInstant = type === "instant";
  return (
    <span
      style={{
        color: "#2e9e8a",
        fontSize: "13px",
        display: "flex",
        alignItems: "center",
        gap: "5px",
      }}
    >
      {isInstant ? (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
          <path d="M7.5 1L2 7.5h4.5L5 12l6.5-6.5H7L7.5 1z" fill="#2e9e8a" />
        </svg>
      ) : (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
          <circle
            cx="6.5"
            cy="6.5"
            r="5.5"
            stroke="#2e9e8a"
            strokeWidth="1.3"
          />
          <path
            d="M6.5 3.5v3l2 1.5"
            stroke="#2e9e8a"
            strokeWidth="1.3"
            strokeLinecap="round"
          />
        </svg>
      )}
      {isInstant ? "Instant Booking" : "Non Instant booking"}
    </span>
  );
};

const getPaginationPages = (
  current: number,
  last: number,
): (number | "...")[] => {
  if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1);
  const pages: (number | "...")[] = [1, 2];
  if (current > 4) pages.push("...");
  for (
    let i = Math.max(3, current - 1);
    i <= Math.min(last - 2, current + 1);
    i++
  ) {
    pages.push(i);
  }
  if (current < last - 3) pages.push("...");
  pages.push(last - 1, last);
  return [...new Set(pages)];
};

export const CautionFeeTable: React.FC<Props> = ({
  data,
  pagination,
  onPageChange,
  showBookingType = true,
}) => {
  const pages = pagination
    ? getPaginationPages(pagination.current_page, pagination.last_page)
    : [];

  return (
    <>
      <div className="cf-table-wrapper">
        <div style={{ overflowX: "auto" }}>
          <table className="cf-table">
            <thead>
              <tr>
                {showBookingType && <th>Booking Type</th>}
                <th>Property name</th>
                <th>Stay dates</th>
                <th>Caution fee</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i}>
                  {showBookingType && <td>
                    <BookingTypeCell type={row.bookingType} />
                  </td>}
                  <td>{row.propertyName}</td>
                  <td>{row.stayDates.label}</td>
                  <td className="caution-fee-val">
                    ₦{row.cautionFee.toLocaleString()}
                  </td>
                  <td>
                    <StatusBadge status={row.displayStatus} />
                  </td>
                  <td><CautionFeeActions id={row.transactionId} name={row.propertyName} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pagination && (
          <div className="cf-pagination">
            <button
              className="cf-page-btn"
              disabled={pagination.current_page === 1}
              onClick={() =>
                onPageChange && onPageChange(pagination.current_page - 1)
              }
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 12L6 8L10 4"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Previous
            </button>

            <div className="cf-page-numbers">
              {pages.map((p, i) =>
                p === "..." ? (
                  <span key={`ellipsis-${i}`} className="cf-page-ellipsis">
                    ...
                  </span>
                ) : (
                  <button
                    key={p}
                    className={`cf-page-num${pagination.current_page === p ? " active" : ""}`}
                    onClick={() =>
                      pagination.current_page !== p &&
                      onPageChange &&
                      onPageChange(p as number)
                    }
                  >
                    {p}
                  </button>
                ),
              )}
            </div>

            <button
              className="cf-page-btn"
              disabled={pagination.current_page === pagination.last_page}
              onClick={() =>
                onPageChange && onPageChange(pagination.current_page + 1)
              }
            >
              Next
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M6 4L10 8L6 12"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </>
  );
};

// Demo usage
const DEMO_DATA: CautionFeeData[] = [
  {
    transactionId: "1",
    bookingType: "non-instant",
    propertyName: "A and B Complex",
    stayDates: { label: "Oct 28 - Nov 03, 2025" },
    cautionFee: 200000,
    displayStatus: "Eligible",
  },
  {
    transactionId: "2",
    bookingType: "instant",
    propertyName: "Yeat apartment",
    stayDates: { label: "Oct 12 - Oct 18, 2025" },
    cautionFee: 200000,
    displayStatus: "Under review",
  },
  {
    transactionId: "3",
    bookingType: "instant",
    propertyName: "Yeat apartment",
    stayDates: { label: "Oct 12 - Oct 18, 2025" },
    cautionFee: 200000,
    displayStatus: "Disputed",
  },
  {
    transactionId: "4",
    bookingType: "instant",
    propertyName: "Yeat apartment",
    stayDates: { label: "Sep 28 - Oct 03, 2025" },
    cautionFee: 200000,
    displayStatus: "Held",
  },
  {
    transactionId: "5",
    bookingType: "non-instant",
    propertyName: "A and B Complex",
    stayDates: { label: "Oct 28 - Nov 03, 2025" },
    cautionFee: 200000,
    displayStatus: "Paid",
  },
];

const DEMO_PAGINATION: PaginationData = {
  total: 25,
  per_page: 5,
  current_page: 1,
  last_page: 5,
  from: 1,
  to: 5,
  has_more_pages: true,
};

export default function App() {
  const [page, setPage] = useState(1);
  return (
    <div style={{ padding: "40px", background: "#f5f7f9", minHeight: "100vh" }}>
      <CautionFeeTable
        data={DEMO_DATA}
        pagination={{ ...DEMO_PAGINATION, current_page: page }}
        onPageChange={setPage}
      />
    </div>
  );
}
